/**
 * Option strategy maths for the chain screen — mirrors
 * backend/app/domain/services/option_payoff.py so the payoff chart reacts
 * instantly while the server remains the source of truth for execution.
 */

export type Side = 'B' | 'S';
export type OptionType = 'CE' | 'PE';

export interface StrategyLeg {
  id: string;
  side: Side;
  option_type: OptionType;
  strike: number;
  price: number;
  lots: number;
  instrument_token: string;
  trading_symbol: string;
  exchange_segment: string;
}

export interface PayoffPoint {
  price: number;
  pnl: number;
}

export interface PayoffSummary {
  netPremium: number;
  maxProfit: number | null; // null → unlimited
  maxLoss: number | null; // null → unlimited (negative number when bounded)
  breakevens: number[];
  curve: PayoffPoint[];
  minPrice: number;
  maxPrice: number;
}

function intrinsic(leg: StrategyLeg, price: number): number {
  return leg.option_type === 'CE' ? Math.max(price - leg.strike, 0) : Math.max(leg.strike - price, 0);
}

export function legQuantity(leg: StrategyLeg, lotSize: number): number {
  return Math.max(1, leg.lots) * Math.max(1, lotSize);
}

export function legPayoff(leg: StrategyLeg, price: number, lotSize: number): number {
  const signed = leg.side === 'B' ? legQuantity(leg, lotSize) : -legQuantity(leg, lotSize);
  return (intrinsic(leg, price) - leg.price) * signed;
}

export function totalPayoff(legs: StrategyLeg[], price: number, lotSize: number): number {
  return legs.reduce((sum, leg) => sum + legPayoff(leg, price, lotSize), 0);
}

export function netPremium(legs: StrategyLeg[], lotSize: number): number {
  return legs.reduce((sum, leg) => {
    const qty = legQuantity(leg, lotSize);
    return sum + (leg.side === 'S' ? leg.price * qty : -leg.price * qty);
  }, 0);
}

function endSlopes(legs: StrategyLeg[], lotSize: number): { right: number } {
  let right = 0;
  for (const leg of legs) {
    if (leg.option_type === 'CE') right += leg.side === 'B' ? legQuantity(leg, lotSize) : -legQuantity(leg, lotSize);
  }
  return { right };
}

function breakevens(legs: StrategyLeg[], strikes: number[], rightSlope: number, lotSize: number): number[] {
  const points = [0, ...strikes];
  const values = points.map((p) => totalPayoff(legs, p, lotSize));
  const found: number[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const [p0, p1, v0, v1] = [points[i], points[i + 1], values[i], values[i + 1]];
    if (v0 === 0 && p0 > 0) found.push(p0);
    if ((v0 < 0 && v1 > 0) || (v1 < 0 && v0 > 0)) found.push(p0 + ((p1 - p0) * -v0) / (v1 - v0));
  }
  const lastP = points[points.length - 1];
  const lastV = values[values.length - 1];
  if (lastV === 0) found.push(lastP);
  else if (rightSlope !== 0 && ((lastV < 0 && rightSlope > 0) || (lastV > 0 && rightSlope < 0))) {
    found.push(lastP + -lastV / rightSlope);
  }
  const out: number[] = [];
  for (const b of found.sort((a, c) => a - c)) {
    if (b > 0 && (out.length === 0 || Math.abs(out[out.length - 1] - b) > 0.001)) out.push(b);
  }
  return out;
}

/** Full expiry analysis; `spot` centres the chart window when known. */
export function analyzePosition(
  legs: StrategyLeg[],
  lotSize: number,
  spot?: number | null,
  points = 121,
  rangePct = 0.12
): PayoffSummary {
  if (legs.length === 0) {
    return { netPremium: 0, maxProfit: 0, maxLoss: 0, breakevens: [], curve: [], minPrice: 0, maxPrice: 0 };
  }
  const strikes = [...new Set(legs.map((l) => l.strike))].sort((a, b) => a - b);
  const centre = spot && spot > 0 ? spot : (strikes[0] + strikes[strikes.length - 1]) / 2;
  const span = Math.max(centre * rangePct, (strikes[strikes.length - 1] - strikes[0]) * 0.5, 1);
  const lo = Math.max(0, Math.min(strikes[0], centre - span));
  const hi = Math.max(strikes[strikes.length - 1], centre + span);

  const { right } = endSlopes(legs, lotSize);
  const candidates = [...strikes, lo, hi, 0].map((p) => totalPayoff(legs, p, lotSize));
  let maxProfit: number | null = Math.max(...candidates);
  let maxLoss: number | null = Math.min(...candidates);
  if (right > 0) maxProfit = null;
  if (right < 0) maxLoss = null;

  const bes = breakevens(legs, strikes, right, lotSize);
  const step = (hi - lo) / (points - 1);
  const grid = new Set<number>();
  for (let i = 0; i < points; i++) grid.add(Math.round((lo + step * i) * 100) / 100);
  for (const extra of [...strikes, ...bes]) if (extra >= lo && extra <= hi) grid.add(Math.round(extra * 100) / 100);
  const curve = [...grid].sort((a, b) => a - b).map((price) => ({ price, pnl: Math.round(totalPayoff(legs, price, lotSize) * 100) / 100 }));

  return {
    netPremium: Math.round(netPremium(legs, lotSize) * 100) / 100,
    maxProfit: maxProfit === null ? null : Math.round(maxProfit * 100) / 100,
    maxLoss: maxLoss === null ? null : Math.round(maxLoss * 100) / 100,
    breakevens: bes.map((b) => Math.round(b * 20) / 20),
    curve,
    minPrice: lo,
    maxPrice: hi,
  };
}

// ── Presets ─────────────────────────────────────────────────────────────────

export interface ChainLegQuote {
  instrument_token: string;
  trading_symbol: string;
  exchange_segment: string;
  ltp: string;
}

export interface ChainRowLike {
  strike: string;
  is_atm: boolean;
  ce: ChainLegQuote | null;
  pe: ChainLegQuote | null;
}

export type PresetKind =
  | 'short_straddle'
  | 'long_straddle'
  | 'short_strangle'
  | 'long_strangle'
  | 'bull_call_spread'
  | 'bear_put_spread'
  | 'iron_condor'
  | 'iron_butterfly';

export const PRESETS: Array<{ kind: PresetKind; label: string; hint: string }> = [
  { kind: 'short_straddle', label: 'Short straddle', hint: 'Sell ATM CE + PE · range-bound view' },
  { kind: 'long_straddle', label: 'Long straddle', hint: 'Buy ATM CE + PE · big move expected' },
  { kind: 'short_strangle', label: 'Short strangle', hint: 'Sell OTM CE + PE · wider range' },
  { kind: 'long_strangle', label: 'Long strangle', hint: 'Buy OTM CE + PE · cheaper breakout bet' },
  { kind: 'bull_call_spread', label: 'Bull call spread', hint: 'Buy ATM CE, sell OTM CE · mildly bullish' },
  { kind: 'bear_put_spread', label: 'Bear put spread', hint: 'Buy ATM PE, sell OTM PE · mildly bearish' },
  { kind: 'iron_condor', label: 'Iron condor', hint: 'Short strangle with wings · defined risk' },
  { kind: 'iron_butterfly', label: 'Iron butterfly', hint: 'Short straddle with wings · defined risk' },
];

let legSeq = 0;
export function newLegId(): string {
  legSeq += 1;
  return `leg-${Date.now().toString(36)}-${legSeq}`;
}

function mkLeg(row: ChainRowLike, type: OptionType, side: Side, lots: number): StrategyLeg | null {
  const q = type === 'CE' ? row.ce : row.pe;
  if (!q) return null;
  return {
    id: newLegId(),
    side,
    option_type: type,
    strike: Number(row.strike),
    price: Number(q.ltp) || 0,
    lots,
    instrument_token: q.instrument_token,
    trading_symbol: q.trading_symbol,
    exchange_segment: q.exchange_segment,
  };
}

/** Build a preset around the ATM row; `step` is how many strikes away the OTM legs sit. */
export function buildPreset(kind: PresetKind, rows: ChainRowLike[], lots = 1, step = 2): StrategyLeg[] {
  const atmIndex = rows.findIndex((r) => r.is_atm);
  if (atmIndex < 0) return [];
  const at = (offset: number) => rows[Math.min(rows.length - 1, Math.max(0, atmIndex + offset))];
  const legs: Array<StrategyLeg | null> = [];
  switch (kind) {
    case 'short_straddle':
      legs.push(mkLeg(at(0), 'CE', 'S', lots), mkLeg(at(0), 'PE', 'S', lots));
      break;
    case 'long_straddle':
      legs.push(mkLeg(at(0), 'CE', 'B', lots), mkLeg(at(0), 'PE', 'B', lots));
      break;
    case 'short_strangle':
      legs.push(mkLeg(at(step), 'CE', 'S', lots), mkLeg(at(-step), 'PE', 'S', lots));
      break;
    case 'long_strangle':
      legs.push(mkLeg(at(step), 'CE', 'B', lots), mkLeg(at(-step), 'PE', 'B', lots));
      break;
    case 'bull_call_spread':
      legs.push(mkLeg(at(0), 'CE', 'B', lots), mkLeg(at(step), 'CE', 'S', lots));
      break;
    case 'bear_put_spread':
      legs.push(mkLeg(at(0), 'PE', 'B', lots), mkLeg(at(-step), 'PE', 'S', lots));
      break;
    case 'iron_condor':
      legs.push(
        mkLeg(at(step), 'CE', 'S', lots),
        mkLeg(at(step * 2), 'CE', 'B', lots),
        mkLeg(at(-step), 'PE', 'S', lots),
        mkLeg(at(-step * 2), 'PE', 'B', lots)
      );
      break;
    case 'iron_butterfly':
      legs.push(
        mkLeg(at(0), 'CE', 'S', lots),
        mkLeg(at(step), 'CE', 'B', lots),
        mkLeg(at(0), 'PE', 'S', lots),
        mkLeg(at(-step), 'PE', 'B', lots)
      );
      break;
  }
  return legs.filter((l): l is StrategyLeg => l !== null);
}

/** A readable name for an arbitrary set of legs (used as the order tag). */
export function describeLegs(legs: StrategyLeg[]): string {
  if (legs.length === 0) return 'Custom strategy';
  const key = legs
    .map((l) => `${l.side}${l.option_type}`)
    .sort()
    .join(',');
  const sameStrike = new Set(legs.map((l) => l.strike)).size === 1;
  if (legs.length === 2) {
    if (key === 'SCE,SPE') return sameStrike ? 'Short straddle' : 'Short strangle';
    if (key === 'BCE,BPE') return sameStrike ? 'Long straddle' : 'Long strangle';
    if (key === 'BCE,SCE') return 'Call spread';
    if (key === 'BPE,SPE') return 'Put spread';
  }
  if (legs.length === 4 && key === 'BCE,BPE,SCE,SPE') return sameStrike ? 'Iron butterfly' : 'Iron condor';
  if (legs.length === 1) return `${legs[0].side === 'B' ? 'Long' : 'Short'} ${legs[0].option_type}`;
  return `Custom ${legs.length}-leg`;
}
